/**
 * Signup plan comparison table — rendered from PlanFeatureCatalog (public page, no session).
 */

import { PLAN_LIMITS } from "../plan_control/plan-limits.js";

const PLAN_ORDER = ["basic", "professional", "premium"];
const EXPANDED_CLASS = "signup-plan-comparison--expanded";

const TIER_SECTIONS = {
  basic: "Incluído no Básico",
  professional: "A partir do Profissional",
  premium: "Somente no Premium",
};

const TIER_BLOCKS = {
  basic: [
    {
      name: "Atendimento",
      keys: [
        "funil.reminders",
        "leads.crud",
        "leads.relationship_dates",
        "communication.history",
        "whatsapp.deeplink",
        "whatsapp.templates",
      ],
    },
    {
      name: "Dados",
      keys: ["kpi.essential", "leads.import", "export.data", "admin.tools"],
    },
  ],
  professional: [
    {
      name: "Retomada de contato",
      keys: ["automations.contact_rules", "automations.birthday_funnel"],
    },
    {
      name: "Análise",
      keys: [
        "kpi.full",
        "scoring.lead",
        "scoring.followup",
        "search.advanced",
        "smart.review",
        "reports.attendance",
      ],
    },
    {
      name: "Operação",
      keys: ["input.voice_dictation", "tasks.contact"],
    },
    {
      name: "Integrações",
      keys: ["integrations.api"],
    },
  ],
  premium: [
    {
      name: "Retomada de contato",
      keys: ["automations.contact_rules_advanced", "automations.repurchase_window"],
    },
    {
      name: "Inteligência",
      keys: ["ai.message_draft", "ai.approach_tip"],
    },
    {
      name: "Integrações",
      keys: [
        "integrations.meta",
        "integrations.letscall",
        "integrations.rdstation",
        "integrations.feegow",
        "integrations.citizen_card",
      ],
    },
    {
      name: "Equipe",
      keys: ["leads.assignment", "achievements.team", "achievements.personal"],
    },
  ],
};

function getCatalog() {
  return window.PlanFeatureCatalog || null;
}

function sortByLabel(features) {
  return [...features].sort((a, b) => a.label.localeCompare(b.label, "pt"));
}

function pickFeaturesByKeys(featuresByKey, keys) {
  return keys.map((key) => featuresByKey.get(key)).filter(Boolean);
}

/**
 * @param {Array<{ key: string, min_plan?: string, label: string }>} features
 */
function groupFeaturesForSignupTable(features) {
  const buckets = {
    basic: [],
    professional: [],
    premium: [],
  };

  features.forEach((feature) => {
    const planKey = feature.min_plan || "basic";
    if (buckets[planKey]) {
      buckets[planKey].push(feature);
    } else {
      buckets.basic.push(feature);
    }
  });

  return PLAN_ORDER.map((planKey) => {
    const featuresByKey = new Map(buckets[planKey].map((item) => [item.key, item]));
    const definedBlocks = TIER_BLOCKS[planKey] || [];
    const assignedKeys = new Set(definedBlocks.flatMap((block) => block.keys));
    const blocks = definedBlocks
      .map((block) => ({
        name: block.name,
        features: pickFeaturesByKeys(featuresByKey, block.keys),
      }))
      .filter((block) => block.features.length > 0);
    const leftover = sortByLabel(
      buckets[planKey].filter((item) => !assignedKeys.has(item.key))
    );
    if (leftover.length > 0) {
      blocks.push({ name: null, features: leftover });
    }
    return { name: TIER_SECTIONS[planKey], blocks };
  });
}

function buildPlanHeaderCells(catalog) {
  return PLAN_ORDER.map((planKey) => {
    const label = catalog.PLANS[planKey]?.label || planKey;
    const th = document.createElement("th");
    th.scope = "col";
    th.className = "signup-plan-comparison-plan-col";
    th.textContent = label;
    return th;
  });
}

function buildFeatureCell(included) {
  const td = document.createElement("td");
  td.className = included
    ? "signup-plan-comparison-cell signup-plan-check"
    : "signup-plan-comparison-cell signup-plan-miss";

  const span = document.createElement("span");
  span.setAttribute("aria-label", included ? "Incluído" : "Não incluído");
  span.textContent = included ? "✓" : "—";
  td.appendChild(span);
  return td;
}

function buildFeatureLabelCell(feature) {
  const labelCell = document.createElement("th");
  labelCell.scope = "row";
  labelCell.className = "signup-plan-comparison-feature-col";

  const row = document.createElement("div");
  row.className = "signup-plan-feature-label-row";

  const label = document.createElement("span");
  label.className = "signup-plan-feature-label";
  label.textContent = feature.label;
  row.appendChild(label);

  if (feature.description) {
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "plan-feature-info-trigger";
    trigger.setAttribute("data-plan-feature-info", feature.key);
    trigger.setAttribute("aria-label", `Saiba mais: ${feature.label}`);
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-controls", "plan-feature-info-popover");

    const icon = document.createElement("span");
    icon.className = "material-icons";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "info";
    trigger.appendChild(icon);
    row.appendChild(trigger);
  }

  labelCell.appendChild(row);
  return labelCell;
}

function buildLimitValueCell(value) {
  const td = document.createElement("td");
  td.className = "signup-plan-comparison-cell signup-plan-limit";
  td.textContent = String(value);
  return td;
}

function appendGroupRow(tbody, name, { nested = false } = {}) {
  const groupRow = document.createElement("tr");
  groupRow.className = nested
    ? "signup-plan-group-row signup-plan-group-row--nested"
    : "signup-plan-group-row";
  const groupCell = document.createElement("td");
  groupCell.colSpan = PLAN_ORDER.length + 1;
  groupCell.textContent = name;
  groupRow.appendChild(groupCell);
  tbody.appendChild(groupRow);
}

function appendFeatureRow(tbody, catalog, feature) {
  const row = document.createElement("tr");
  row.appendChild(buildFeatureLabelCell(feature));

  PLAN_ORDER.forEach((planKey) => {
    const included = catalog.canUseFeature(planKey, feature.key);
    row.appendChild(buildFeatureCell(included));
  });

  tbody.appendChild(row);
}

function buildLimitsSection(tbody) {
  const limits = PLAN_LIMITS;

  appendGroupRow(tbody, "Limites inclusos");

  const seatRow = document.createElement("tr");
  const seatLabel = document.createElement("th");
  seatLabel.scope = "row";
  seatLabel.className = "signup-plan-comparison-feature-col";
  seatLabel.textContent = "Usuários inclusos";
  seatRow.appendChild(seatLabel);
  PLAN_ORDER.forEach((planKey) => {
    seatRow.appendChild(buildLimitValueCell(limits[planKey]?.maxSeats ?? "—"));
  });
  tbody.appendChild(seatRow);

  const locRow = document.createElement("tr");
  const locLabel = document.createElement("th");
  locLabel.scope = "row";
  locLabel.className = "signup-plan-comparison-feature-col";
  locLabel.textContent = "Localidades Google";
  locRow.appendChild(locLabel);
  PLAN_ORDER.forEach((planKey) => {
    locRow.appendChild(buildLimitValueCell(limits[planKey]?.maxGoogleLocations ?? "—"));
  });
  tbody.appendChild(locRow);
}

function buildComparisonTable(catalog) {
  const features = catalog.featuresForSignupTable();
  const groups = groupFeaturesForSignupTable(features);

  const table = document.createElement("table");
  table.className = "signup-plan-comparison-table";

  const caption = document.createElement("caption");
  caption.className = "sr-only";
  caption.textContent = "Comparativo de recursos por plano";
  table.appendChild(caption);

  const thead = document.createElement("thead");
  const headerRow = document.createElement("tr");
  const featureHeader = document.createElement("th");
  featureHeader.scope = "col";
  featureHeader.className = "signup-plan-comparison-feature-col";
  featureHeader.textContent = "Recurso";
  headerRow.appendChild(featureHeader);
  buildPlanHeaderCells(catalog).forEach((th) => headerRow.appendChild(th));
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  buildLimitsSection(tbody);
  groups.forEach((group) => {
    appendGroupRow(tbody, group.name);

    group.blocks.forEach((block) => {
      if (block.name) {
        appendGroupRow(tbody, block.name, { nested: true });
      }
      block.features.forEach((feature) => appendFeatureRow(tbody, catalog, feature));
    });
  });

  table.appendChild(tbody);
  return table;
}

function buildScrollWrapper(table) {
  const scroll = document.createElement("div");
  scroll.className = "signup-plan-comparison-scroll";
  scroll.appendChild(table);
  return scroll;
}

/**
 * @param {HTMLElement|null} containerEl
 * @param {{ collapsed?: boolean }} [options]
 * @returns {boolean}
 */
export function renderSignupPlanComparison(containerEl, options = {}) {
  if (!containerEl) return false;

  const collapsed = options.collapsed !== false;
  containerEl.innerHTML = "";
  containerEl.classList.toggle(EXPANDED_CLASS, !collapsed);

  const catalog = getCatalog();
  if (!catalog) {
    window.Logger?.warn?.("SignupPlanComparison: PlanFeatureCatalog not loaded.");
    return false;
  }

  const section = document.createElement("section");
  section.className = "signup-plan-comparison-inner";
  section.setAttribute("aria-labelledby", "signupPlanComparisonTitle");

  const title = document.createElement("h3");
  title.id = "signupPlanComparisonTitle";
  title.className = "signup-plan-comparison-title";
  title.textContent = "Compare os recursos incluídos";

  const lead = document.createElement("p");
  lead.className = "signup-plan-comparison-lead";
  lead.textContent =
    "Do Básico ao Premium: cada plano inclui o anterior e adiciona novos recursos. Preços ficam na tabela acima.";

  const details = document.createElement("details");
  details.className = "signup-plan-comparison-details";
  details.id = "signupPlanComparisonDetails";
  if (!collapsed) {
    details.open = true;
  }

  const summary = document.createElement("summary");
  summary.className = "signup-plan-comparison-summary";
  summary.textContent = "Ver comparativo completo de recursos";

  const table = buildComparisonTable(catalog);
  const scroll = buildScrollWrapper(table);

  details.appendChild(summary);
  details.appendChild(scroll);

  details.addEventListener("toggle", () => {
    containerEl.classList.toggle(EXPANDED_CLASS, details.open);
  });

  section.appendChild(title);
  section.appendChild(lead);
  section.appendChild(details);
  containerEl.appendChild(section);

  if (window.PlanFeatureInfoPopover && typeof window.PlanFeatureInfoPopover.setup === "function") {
    window.PlanFeatureInfoPopover.setup(containerEl);
  }

  return true;
}

export function initSignupPlanComparison() {
  const container = document.getElementById("signupPlanComparison");
  if (!container) return;
  renderSignupPlanComparison(container, { collapsed: true });
}
